import React, { useState, useEffect } from 'react';
import { Star, Trophy, Calendar, MapPin, Play, Instagram, Youtube, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { athletesAPI } from '../services/api';

const Talents = () => {
  const { t, language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [athletes, setAthletes] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAthletes();
  }, [selectedCategory, language]);

  const fetchAthletes = async () => {
    try {
      setLoading(true);
      const params: any = { lang: language };
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      
      const response = await athletesAPI.getAll(params);
      if (response.records) {
        setAthletes(response.records);
        
        // Update categories count
        const categoryCounts = {
          all: response.pagination.total_records,
          professional: response.records.filter((a: any) => a.category === 'professional').length,
          emerging: response.records.filter((a: any) => a.category === 'emerging').length
        };
        
        setCategories([
          { id: 'all', name: t('talents.filter.all'), count: categoryCounts.all }]);
      }
    } catch (error) {
      console.error('Error fetching athletes:', error);
      // Fallback to mock data
      const mockAthletes = [
        {
          id: 1,
          name: 'Cherif Fall',
          country: 'Sénégal',
          category: 'professional',
          age: 24,
          specialty: 'Shortboard',
          ranking: 1,
          image_url: 'https://images.pexels.com/photos/1308881/pexels-photo-1308881.jpeg?auto=compress&cs=tinysrgb&w=600',
          bio: 'Prodige du surf sénégalais, Cherif Fall incarne la nouvelle génération de surfeurs africains. Son style fluide et sa détermination en font un compétiteur redoutable.',
          achievements: 'Champion Africa Surf Tour 2024,Qualification WSL Challenger Series,3x Champion Sénégal',
          sponsors: 'Quiksilver,Red Bull,FCS',
          social_instagram: '@cheriffall_surf',
          social_youtube: 'Cherif Fall Surf',
          stats_competitions: 28,
          stats_wins: 12,
          stats_podiums: 20
        },
        {
          id: 2,
          name: 'Aminata Diop',
          country: 'Sénégal',
          category: 'professional',
          age: 22,
          specialty: 'Shortboard',
          ranking: 1,
          image_url: 'https://images.pexels.com/photos/1772123/pexels-photo-1772123.jpeg?auto=compress&cs=tinysrgb&w=600',
          bio: 'Pionnière du surf féminin au Sénégal, Aminata inspire toute une génération de jeunes filles à embrasser les vagues.',
          achievements: 'Championne Africa Surf Tour 2024,Ambassadrice Jeunesse ASCsurf,Record national féminin',
          sponsors: 'Roxy,Billabong,Futures',
          social_instagram: '@aminata_surf',
          social_youtube: 'Aminata Diop',
          stats_competitions: 22,
          stats_wins: 8,
          stats_podiums: 15
        }
      ];
      
      setAthletes(mockAthletes);
      setCategories([
        { id: 'all', name: t('talents.filter.all'), count: mockAthletes.length },
        { id: 'professional', name: t('talents.filter.professional'), count: mockAthletes.filter(a => a.category === 'professional').length },
        { id: 'emerging', name: t('talents.filter.emerging'), count: mockAthletes.filter(a => a.category === 'emerging').length }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAthletes = selectedCategory === 'all' 
    ? athletes 
    : athletes.filter(a => a.category === selectedCategory);

  const developmentPrograms = [
    {
      title: 'Programme Jeunes Talents',
      description: 'Formation complète pour les surfeurs de 12 à 18 ans',
      participants: 150,
      icon: Star,
      features: ['Coaching technique', 'Préparation physique', 'Mentorat pro', 'Équipement fourni']
    },
    {
      title: 'Académie Professionnelle',
      description: 'Préparation aux circuits internationaux',
      participants: 45,
      icon: Trophy,
      features: ['Entraîneur WSL', 'Analyse vidéo', 'Préparation mentale', 'Gestion carrière']
    },
    {
      title: 'Surf Féminin',
      description: 'Développement spécifique du surf féminin africain',
      participants: 80,
      icon: Star,
      features: ['Modèles féminins', 'Coaching adapté', 'Équipement spécialisé', 'Réseau international']
    }
  ];

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 text-white relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            {t('talents.hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-orange-100 max-w-3xl mx-auto mb-8">
             {t('talents.hero.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-lg">
              <div className="text-2xl font-bold text-white">{athletes.length}</div>
              <div className="text-sm text-orange-200"> {t('talents.hero.athletes')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-lg">
              <div className="text-2xl font-bold text-white">
                {Array.from(new Set(athletes.map(a => a.country))).length}
              </div>
              <div className="text-sm text-orange-200"> {t('talents.hero.country')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-orange-500 text-white shadow-lg transform scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Athletes Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredAthletes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Aucun athlète trouvé pour cette catégorie.</p>
              <button 
                onClick={() => setSelectedCategory('all')}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg"
              >
                Voir tous les athlètes
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAthletes.map((athlete) => (
                <div 
                  key={athlete.id} 
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col"
                >
                  {/* Athlete Photo as Background */}
                  <div 
                    className="relative w-full h-96 bg-contain bg-center"
                    style={{ backgroundImage: `url(${athlete.image_url})` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white">{athlete.name}</h3>
                      {athlete.social_instagram && (
                        <a 
                          href={`https://instagram.com/${athlete.social_instagram.replace('@', '')}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center text-orange-200 hover:text-orange-300 transition-colors mt-1"
                        >
                          <Instagram className="h-5 w-5 mr-2" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-red-500">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('talents.become.title')}
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
             {t('talents.become.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href='/contact' className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105">
              {t('talents.become.apply')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Talents;